import type {
  AdminUser,
  CreateUserPayload,
  GrantPermissionPayload,
  MeResponse,
} from '../types/auth';
import type { NodeDetailResponse } from '../types/nodeConfig';
import type {
  ApiError,
  HealthResponse,
  ProjectDocument,
  ProjectRecord,
  ProjectSummary,
  UploadResponse,
} from '../types/project';
import type { ValidationIssue, ValidationReport } from '../types/validation';
import type {
  ApplyDiagnosticsResponse,
  DiagnosticAction,
  DiagnosticReport,
} from '../types/diagnostics';

// ------------------------------------------------------------
// API client — talks to /api/* (Vite proxy in dev, same origin in prod)
// ------------------------------------------------------------

const defaultFetchInit: RequestInit = {
  credentials: 'include',
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...defaultFetchInit,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    ...init,
  });

  if (!response.ok) {
    let message = response.statusText;
    let validation: ValidationReport | undefined;
    let issues: ValidationIssue[] | undefined;
    try {
      const body = (await response.json()) as ApiError;
      message = body.error ?? message;
      validation = body.validation;
      issues = body.issues ?? validation?.issues;
    } catch {
      // ignore parse errors
    }
    const err = new Error(message) as Error & {
      validation?: ValidationReport;
      issues?: ValidationIssue[];
    };
    err.validation = validation ?? (issues ? { issues } : undefined);
    err.issues = issues;
    throw err;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export async function fetchHealth(): Promise<HealthResponse> {
  return request<HealthResponse>('/health');
}

export async function fetchMe(): Promise<MeResponse> {
  return request<MeResponse>('/api/auth/me');
}

export async function login(
  email: string,
  password: string,
): Promise<MeResponse> {
  return request<MeResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function logout(): Promise<void> {
  await request<void>('/api/auth/logout', { method: 'POST' });
}

export async function fetchUserPreferences(): Promise<{ preferences: Record<string, unknown> }> {
  return request<{ preferences: Record<string, unknown> }>('/api/auth/me/preferences');
}

export async function updateUserPreferences(
  patch: Record<string, unknown>,
): Promise<{ preferences: Record<string, unknown> }> {
  return request<{ preferences: Record<string, unknown> }>('/api/auth/me/preferences', {
    method: 'PUT',
    body: JSON.stringify({ preferences: patch }),
  });
}

export async function listAdminUsers(): Promise<AdminUser[]> {
  return request<AdminUser[]>('/api/admin/users');
}

export async function createUser(payload: CreateUserPayload): Promise<AdminUser> {
  return request<AdminUser>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function setUserActive(
  userId: string,
  active: boolean,
): Promise<AdminUser> {
  return request<AdminUser>(`/api/admin/users/${userId}/active`, {
    method: 'PUT',
    body: JSON.stringify({ active }),
  });
}

export async function grantUserPermission(
  userId: string,
  payload: GrantPermissionPayload,
): Promise<void> {
  await request<void>(`/api/admin/users/${userId}/permissions`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function revokeUserPermission(
  userId: string,
  projectId: string,
): Promise<void> {
  await request<void>(
    `/api/admin/users/${userId}/permissions/${projectId}`,
    { method: 'DELETE' },
  );
}

export async function listProjects(
  includeArchived = false,
): Promise<ProjectSummary[]> {
  const query = includeArchived ? '?include_archived=true' : '';
  return request<ProjectSummary[]>(`/api/projects${query}`);
}

export async function createProject(
  name: string,
  description?: string,
): Promise<ProjectRecord> {
  return request<ProjectRecord>('/api/projects', {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  });
}

export async function getProject(id: string): Promise<ProjectRecord> {
  return request<ProjectRecord>(`/api/projects/${id}`);
}

export async function updateProject(
  id: string,
  payload: {
    name?: string;
    description?: string;
    document?: ProjectDocument;
  },
): Promise<ProjectRecord> {
  return request<ProjectRecord>(`/api/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function duplicateProject(id: string): Promise<ProjectRecord> {
  return request<ProjectRecord>(`/api/projects/${id}/duplicate`, {
    method: 'POST',
  });
}

export async function archiveProject(
  id: string,
  archived: boolean,
): Promise<ProjectRecord> {
  return request<ProjectRecord>(`/api/projects/${id}/archive`, {
    method: 'POST',
    body: JSON.stringify({ archived }),
  });
}

export function uploadUrl(id: string): string {
  return `/api/uploads/${id}`;
}

export async function validateProject(
  id: string,
): Promise<ValidationReport> {
  return request<ValidationReport>(`/api/projects/${id}/validate`, {
    method: 'POST',
  });
}

export async function scanProjectDiagnostics(
  id: string,
): Promise<DiagnosticReport> {
  return request<DiagnosticReport>(`/api/projects/${id}/diagnostics`, {
    method: 'POST',
  });
}

export async function applyProjectDiagnostics(
  id: string,
  body: { actions?: DiagnosticAction[]; fix_all_safe?: boolean },
): Promise<ApplyDiagnosticsResponse> {
  return request<ApplyDiagnosticsResponse>(
    `/api/projects/${id}/diagnostics/apply`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export async function getNode(
  projectId: string,
  nodeId: string,
): Promise<NodeDetailResponse> {
  return request<NodeDetailResponse>(
    `/api/projects/${projectId}/nodes/${nodeId}`,
  );
}

export async function createSimulationRun(
  projectId: string,
  payload?: {
    duration_seconds?: number;
    run_until_stopped?: boolean;
    scenario_id?: string;
    scenario_overrides?: unknown;
    async_mode?: boolean;
    visual_fps?: number;
    stream_interval_ms?: number;
    realtime?: boolean;
    time_scale?: number;
  },
): Promise<{
  id: string;
  status: string;
  validation: ValidationReport;
  scenario_id?: string;
  scenario_name?: string;
  progress_pct?: number;
}> {
  return request(`/api/projects/${projectId}/simulation-runs`, {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  });
}

export async function getSimulationStatus(runId: string): Promise<{
  run_id: string;
  status: string;
  progress_pct: number;
  finished: boolean;
}> {
  return request(`/api/simulation-runs/${runId}/status`);
}

export function simulationStreamUrl(runId: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/simulation-runs/${runId}/stream`;
}

export async function listScenarios(
  projectId: string,
): Promise<{ scenarios: import('../types/scenario').ScenarioPreset[] }> {
  return request(`/api/projects/${projectId}/scenarios`);
}

export async function updateScenarios(
  projectId: string,
  scenarios: import('../types/scenario').ScenarioPreset[],
): Promise<{ scenarios: import('../types/scenario').ScenarioPreset[] }> {
  return request(`/api/projects/${projectId}/scenarios`, {
    method: 'PUT',
    body: JSON.stringify({ scenarios }),
  });
}

export async function getSimulationResults(runId: string): Promise<{
  run_id: string;
  status: string;
  summary: unknown;
  timeseries: unknown[];
}> {
  return request(`/api/simulation-runs/${runId}/results`);
}

export async function cancelSimulationRun(runId: string): Promise<{
  run_id: string;
  status: string;
  progress_pct: number;
  finished: boolean;
}> {
  return request(`/api/simulation-runs/${runId}/cancel`, { method: 'POST' });
}

export function simulationExportUrl(runId: string, format: 'csv' | 'json'): string {
  return `/api/simulation-runs/${runId}/export?format=${format}`;
}

export async function uploadLayoutImage(
  file: File,
  projectId: string,
): Promise<UploadResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('project_id', projectId);

  const response = await fetch('/api/uploads/layout-image', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  if (!response.ok) {
    let message = response.statusText;
    try {
      const body = (await response.json()) as ApiError;
      message = body.error ?? message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  return response.json() as Promise<UploadResponse>;
}
